import ApiService from '@core/api/services/ApiService';
import UrlResolver from '@core/url-resolver/UrlResolver';
import Button from '@stories/Components/Buttons/Button/Button';
import FormField from '@stories/Components/Forms/FormField/FormField';
import Recaptcha from '@stories/Components/Forms/Recaptcha/Recaptcha';
import TextArea from '@stories/Components/Forms/TextArea/TextArea';
import Textbox from '@stories/Components/Forms/Textbox/Textbox';
import Heading from '@stories/Components/Global/Typography/Heading';
import Paragraph from '@stories/Components/Global/Typography/Paragraph';
import Loader from '@stories/Components/Misc/Loader/Loader';
import React, { useEffect, useState } from 'react';
import { ContactUsStyles as S } from '../ContactUs/ContactUs.styles';
const ContactUsForm = ({ title, confirmationTitle, confirmationMessage, translations, siteId, cultureCode, recaptchaSiteKey, idPrefix = '', onSuccess, submitButtonId, }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [response, setResponse] = useState(null);
    const [success, setSuccess] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [emailAddress, setEmailAddress] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [message, setMessage] = useState('');
    const [recaptcha, setRecaptcha] = useState('');
    function handleRecaptchaChange(token) {
        setRecaptcha(token);
    }
    const getErrorLabel = (field) => {
        if (!response) {
            return null;
        }
        const error = response.validationErrors?.find((x) => x.errorSourceId === field);
        if (!error) {
            return null;
        }
        return (React.createElement(React.Fragment, null,
            React.createElement(S.Error, null, error.message)));
    };
    const submit = async () => {
        setIsLoading(true);
        const params = {
            firstName,
            lastName,
            emailAddress,
            phoneNumber,
            message,
            siteId,
            recaptcha,
        };
        const response = await ApiService.request({
            baseUrl: UrlResolver.getDomain(),
            method: 'POST',
            slug: 'submit',
            controller: 'contact',
            params,
        });
        setTimeout(() => {
            // We need to give the animation at least a second otherwise it visually jumps/snaps to the confirmation message //
            setIsLoading(false);
            if (response.status.success === true) {
                setSuccess(true);
                onSuccess?.();
            }
            setResponse(response);
        }, 1000);
    };
    const handleUnload = async () => {
        const cookieConsent = document.cookie.match(new RegExp(`(^| )CookieScriptConsent=([^;]+)`));
        if (!cookieConsent) {
            return;
        }
        const consentJson = JSON.parse(cookieConsent[2]);
        if (!consentJson.categories.includes('targeting')) {
            return;
        }
        if (firstName === '' ||
            lastName === '' ||
            emailAddress === '' ||
            phoneNumber === '' ||
            message === '') {
            await ApiService.request({
                baseUrl: UrlResolver.getDomain(),
                slug: 'partial-contact-submit',
                controller: 'contact',
                method: 'POST',
                params: {
                    firstName: firstName,
                    lastName: lastName,
                    emailAddress: emailAddress,
                    phoneNumber: phoneNumber,
                    message: message,
                    siteId,
                    cultureCode,
                    formName: title,
                },
                keepAlive: true,
            });
        }
    };
    useEffect(() => {
        window.addEventListener('beforeunload', handleUnload, false);
        return () => window.removeEventListener('beforeunload', handleUnload, false);
    }, [firstName, lastName, emailAddress, phoneNumber, message]);
    if (isLoading) {
        return React.createElement(Loader, null);
    }
    if (success) {
        return (React.createElement(S.ConfirmationWrapper, { layout: "size", key: `ContactUsWidget_ConfirmationWrapper` },
            React.createElement(Heading, { semantic: "h3", visual: "h1" }, confirmationTitle),
            React.createElement(Paragraph, { size: "regular" }, confirmationMessage)));
    }
    return (React.createElement(S.FormGrid, { layout: "size", key: `ContactUsWidget_FormGrid` },
        React.createElement(S.Field, null,
            React.createElement(FormField, null,
                React.createElement(Textbox, { id: `${idPrefix}firstName`, type: "text", value: firstName, onChange: (e) => setFirstName(e.currentTarget.value), label: translations['custom.forms.firstname'] })),
            getErrorLabel('firstName')),
        React.createElement(S.Field, null,
            React.createElement(FormField, null,
                React.createElement(Textbox, { id: `${idPrefix}lastName`, type: "text", value: lastName, onChange: (e) => setLastName(e.currentTarget.value), label: translations['custom.forms.lastname'] })),
            getErrorLabel('lastName')),
        React.createElement(S.Field, null,
            React.createElement(FormField, null,
                React.createElement(Textbox, { id: `${idPrefix}email`, type: "email", value: emailAddress, onChange: (e) => setEmailAddress(e.currentTarget.value), label: translations['custom.forms.emailaddress'] })),
            getErrorLabel('emailAddress')),
        React.createElement(S.Field, null,
            React.createElement(FormField, null,
                React.createElement(Textbox, { id: `${idPrefix}phone`, type: "tel", value: phoneNumber, onChange: (e) => setPhoneNumber(e.currentTarget.value), label: translations['custom.forms.phonenumber'] })),
            getErrorLabel('phoneNumber')),
        React.createElement(S.Field, { wide: true },
            React.createElement(FormField, null,
                React.createElement(TextArea, { id: `${idPrefix}message`, label: translations['custom.forms.message'], value: message, onChange: (e) => setMessage(e.currentTarget.value) }),
                getErrorLabel('message'))),
        React.createElement(S.Field, { wide: true },
            React.createElement(FormField, null,
                React.createElement(Recaptcha, { shouldLoad: true, siteKey: recaptchaSiteKey, value: recaptcha, onChange: handleRecaptchaChange }),
                getErrorLabel('recaptcha'))),
        React.createElement(S.Field, { wide: true, center: true },
            React.createElement(FormField, null,
                React.createElement(Button, { id: submitButtonId, title: translations['custom.forms.submit'], branding: "primaryAlt", onClick: submit })))));
};
export default ContactUsForm;
